<script lang="ts" setup>
import { VToolbar, VAppBar } from 'vuetify/components'
import SkrButton from './button/SkrButton.vue'
import type { RouteLocationRaw } from '#vue-router'

const props = withDefaults(
  defineProps<{
    action?: () => void
    business?: 'dark' | 'light'
    dismissible?: boolean
    isLogoClickable?: boolean
    logoOnly?: boolean
    overlay?: boolean
    to?: RouteLocationRaw
    bordered?: boolean
  }>(),
  {
    dismissible: false,
    isLogoClickable: true,
    logoOnly: false,
    overlay: false,
    bordered: false,
  }
)

const route = useRoute()

const businessStore = useBusinessStore()
const { id: userId } = storeToRefs(useUserStore())
const { isDirectSign } = useDirectSignStore()
const { resetSnackbars } = useWidgetStore()

const invitingBusinessId = inject(INVITING_BUSINESS_ID, ref(''))

const { data: publicBusinessInfo } = usePublicInformation(invitingBusinessId, {
  watch: [invitingBusinessId],
})

const srBrandingLogoUrl = computed(() => publicBusinessInfo.value?.companyBranding?.logo?.url ?? '')

const isLogin = computed(() => route.name === 'login')

// The logo in the top left has the following priority:
// 1. company branding of logged in user
// 2. company branding of current signatureRequest and an overlay is shown at the moment
// 3. Skribble logo which is chosen if empty string is sent
const brandingLogoUrl = computed(() => {
  const logoUrl = businessStore.settings.companyBranding?.logo?.url

  if (logoUrl) {
    return logoUrl
  } else if (srBrandingLogoUrl.value && props.overlay) {
    return srBrandingLogoUrl.value
  } else {
    return ''
  }
})

const close = async () => {
  resetSnackbars()
  if (props.action) props.action()
  if (props.to) await navigateTo(props.to)
}

const baseComponent = computed(() => {
  return props.overlay ? VToolbar : VAppBar
})
</script>

<template>
  <!--
    absolute:       on tablets and up
    app:            not for overlay (is not part of app layout)
    clipped-right:  to extend to entire width within overlay
    fixed:          on phones when not within an overlay
  -->
  <component
    :is="baseComponent"
    :absolute="$vuetify.display.smAndUp"
    class="sh"
    :class="{ 'is-dismissible': dismissible, 'is-bordered': bordered }"
    :color="$vuetify.display.smAndUp ? 'transparent' : 'white'"
    :fixed="$vuetify.display.xs && !overlay"
    :height="$vuetify.display.xs ? '70' : '90'"
    flat
  >
    <!-- Dismissible header / most overlays -->
    <template v-if="dismissible && !isDirectSign">
      <slot name="left">
        <skr-button
          ripple
          type="text"
          :aria-label="$t('global.close')"
          class="sh__close sh__close_hover"
          height="100%"
          data-cy="closeHeader"
          @click="close"
        >
          <v-icon size="24">custom:c_close</v-icon>
        </skr-button>
      </slot>
      <div class="sh__title">
        <slot />
      </div>
      <v-row justify="end">
        <slot name="right" />
      </v-row>
    </template>

    <!-- Regular logged-in header
      Overlays will end up here but should not display anything else then
      the logo centered. So we need to introduce some artificial elements
      within the left slot to make this happen.
    -->
    <template v-else-if="userId && !logoOnly">
      <slot name="left">
        <template v-if="$vuetify.display.smAndDown && !overlay">
          <skr-button
            class="sh__main-nav-trigger ml-0"
            type="text"
            icon
            :ripple="false"
            :aria-label="$t('global.menu')"
          >
            <span />
          </skr-button>
        </template>
        <div v-else-if="$vuetify.display.xs" />
      </slot>

      <!-- For overlays only display logo. Don't show a clickable logo (href) -->
      <template v-if="overlay">
        <div v-if="business" class="text-no-decoration">
          <logo data-cy="indexLogo" class="sh__logo" :admin-logo-variant="business" />
        </div>
        <div v-else class="text-no-decoration">
          <logo data-cy="indexLogo" class="sh__logo" :image="brandingLogoUrl" />
        </div>
      </template>

      <template v-else>
        <nuxt-link v-if="business" :to="{ name: 'business-profile' }" class="text-no-decoration">
          <logo data-cy="indexLogo" class="sh__logo" :admin-logo-variant="business" />
        </nuxt-link>
        <nuxt-link v-else-if="!isLogin" to="/" class="text-no-decoration">
          <logo data-cy="indexLogo" class="sh__logo" :image="brandingLogoUrl" />
        </nuxt-link>
      </template>

      <slot name="right">
        <template v-if="$vuetify.display.xs && !overlay">
          <skr-button
            class="avatar-btn pa-0"
            min-width="auto"
            :ripple="false"
            type="text"
            :to="{ name: business ? 'business-profile' : 'profile' }"
          >
            <avatar-business v-if="business" />
            <avatar v-else />
          </skr-button>
        </template>
        <div v-else-if="$vuetify.display.xs" />
      </slot>
    </template>

    <!-- Logged out / public header / 404 page -->
    <template v-else>
      <slot name="left">
        <nuxt-link v-if="isLogoClickable" class="skr-logo" :to="{ path: '/' }">
          <nuxt-img src="/img/logo-skribble.svg" width="54" height="30" alt="Skribble" />
          <div>Skribble</div>
        </nuxt-link>
        <div v-else class="skr-logo">
          <nuxt-img src="/img/logo-skribble.svg" width="54" height="30" alt="Skribble" />
          <div>Skribble</div>
        </div>
      </slot>
      <slot></slot>
      <v-row justify="end">
        <slot name="right" />
      </v-row>
    </template>
  </component>
</template>

<style lang="scss" scoped>
.sh {
  z-index: 1;

  .avatar {
    @include maw(xs) {
      width: 34px;
      height: 34px;
      margin-right: 4px;
    }
  }

  .avatar-btn {
    width: 40px;
    height: 40px;
    min-width: 0;
    margin: 0;
    padding: 0;
    font-size: 0.875rem;
    &.v-btn--active:before {
      display: none;
    }
  }

  :deep(.v-toolbar__content) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 1.5rem;
  }

  &.is-dismissible,
  &.is-bordered {
    :deep(.v-toolbar__content) {
      border-bottom: 1px solid $c-border;
    }
  }

  &__logo {
    &:first-child {
      margin-left: 16px;
    }
  }

  &__main-nav-trigger {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 60px;
    height: 60px;
    border-radius: 0;
    cursor: pointer;

    &.v-size--default:not(.v-btn--round) {
      min-width: auto;
    }

    @include maw(sm) {
      width: 32px;
    }

    &:hover {
      :deep(.v-btn__overlay) {
        background-color: $c-primary;
        &:before,
        &:after {
          background-color: $c-primary;
        }
      }
    }

    &.v-btn {
      min-width: 0;
      padding: 0;
      margin: 0;
    }

    :deep(.v-btn__content) {
      position: relative;
      display: block;
      width: 26px;
      height: 3px;
      border-radius: 1.5px;
      background-color: $c-text;
      @include maw(sm) {
        max-width: 22px;
      }
      &:before,
      &:after {
        position: absolute;
        left: 0;
        width: 26px;
        height: 3px;
        border-radius: 1.5px;
        background-color: $c-text;
        content: '';
        @include maw(sm) {
          width: 22px;
        }
      }
      &:before {
        top: -8px;
        @include maw(sm) {
          top: -7px;
        }
      }
      &:after {
        bottom: -8px;
        @include maw(sm) {
          bottom: -7px;
        }
      }
    }
  }

  &__title {
    overflow: hidden;
    text-overflow: ellipsis;
    color: $c-text-alt;
  }

  &__signing-status {
    position: absolute;
    right: 2px;
    top: -2px;
    width: 16px;
    height: 16px;
  }

  &__close.v-btn {
    position: relative;
    left: -24px;
    width: $siteheader-height;
    height: $siteheader-height;
    margin: 0;
    padding: 0;
    border-right: 1px solid $c-border;
    border-radius: 0;
    color: black;
    @include maw(xs) {
      height: 70px;
    }

    :deep(.v-btn__content) {
      color: $c-text;
    }

    .v-icon {
      margin: 0;
    }
  }

  &__close_hover {
    &:before {
      background: $c-primary !important;
    }
    &:hover {
      :deep(.v-btn__content) {
        color: $c-primary !important;
      }
    }
  }
}
</style>
